<template>
  <div class="fts-automation-design-modal">
    <!-- automation modal -->
    <fts-design-modal
      full-screen
      :backdrop="false"
      class="fts-event-config-modal"
      @opened="afterShow"
      ref="ftsDesignModal"
    >
      <template #title>
        <!-- event type -->
        <div class="event-type">
          <!-- event type: schedule (docs/scheduling-routine-automation-proposal.md §2) -->
          <template v-if="isSchedule">
            <font-awesome-icon :icon="['fas', 'clock']" class="event-type-icon" />
            <span class="fts-inline-spacer-5"></span>
            <span>On a schedule ·</span>
            <span class="fts-inline-spacer-5"></span>
            <span class="event-type-label-ellipsis" data-schedule-sentence>{{ scheduleSentence }}</span>
          </template>
          <!-- event type: vector -->
          <span v-if="eventType === 'vector'">When module is changed</span>
          <!-- event type: information console -->
          <span v-if="eventType === 'informationConsole'">When information is changed</span>
          <!-- event type: activity console -->
          <span v-if="eventType === 'activityConsole'">When activity is performed</span>
          <!-- event type: standard console -->
          <span v-if="eventType === 'standardConsole'">When standard activity is performed</span>
          <!-- event type: state console -->
          <span v-if="eventType === 'stateConsole'">When state is changed</span>
          <!-- event type:activity -->
          <template v-else-if="eventType === 'activity'">
            <span class="event-type-label">When performing</span>
            <span class="fts-inline-spacer-5"></span>
            <div class="event-type-activity">
              <fts-moji
                class="event-type-emoji"
                :emoji="getActivity.emoji"
                :size="18"
                v-if="getActivity && getActivity.emoji"
              />
              <span class="event-type-label-ellipsis">
                {{
                (getActivity && getActivity.name) || "(undefined)"
                }}
              </span>
            </div>
          </template>
          <!-- event type:standardActivity -->
          <template v-else-if="eventType === 'standardActivity'">
            <span>When performing</span>
            <span class="fts-inline-spacer-5" />
            <!-- <div class="event-type-activity"> -->
            <span class="event-type-label-ellipsis">
              {{
              (getStandardActivity && getStandardActivity.name) ||
              "(undefined)"
              }}
            </span>
            <!-- </div> -->
          </template>
          <!-- event type:information -->
          <template v-else-if="eventType === 'information'">
            <span>When</span>
            <span class="fts-inline-spacer-5" />
            <div class="event-type-information">
              <i
                class="event-type-icon"
                :class="informationIcon(getInformation.type)"
                v-if="getInformation"
              ></i>
              <span class="event-type-label-ellipsis">
                {{
                (getInformation && getInformation.displayName) ||
                "(undefined)"
                }}
              </span>
            </div>
            <span class="fts-inline-spacer-5" />
            <span>changed</span>
          </template>
          <!-- event type:state -->
          <template v-else-if="eventType === 'state'">
            <span>When state has</span>
            <span class="fts-inline-spacer-5" />
          </template>
          <!-- event type:due (information date, time) -->
          <template v-else-if="eventType === 'due'">
            <span>When</span>
            <span class="fts-inline-spacer-5"></span>
            <div class="event-type-information">
              <i
                class="event-type-icon"
                :class="informationIcon(getInformation && getInformation.type)"
              ></i>
              <span class="event-type-label-ellipsis">
                {{
                (getInformation && getInformation.displayName) ||
                "(undefined)"
                }}
              </span>
            </div>
            <span class="fts-inline-spacer-5"></span>
            <span>is</span>
            <span class="fts-inline-spacer-5"></span>
          </template>
          <!-- event type:state / due -->
          <template v-if="eventType === 'state' || eventType === 'due'">
            <fts-dropdown-select
              append-to-body
              append-toggle-class="event-type-select__toggle"
              append-content-class="event-type-content event-type-dropdown-menu"
              v-model="formData.trigger"
              :options="triggers"
              :reduce="(x) => x.id"
              class="event-type-select"
            >
              <template #toggle="{ selectedOptionValue }">
                {{ selectedOptionValue }}
                <font-awesome-icon :icon="['fas', 'angle-down']" class="event-type-toggle-icon" />
              </template>
            </fts-dropdown-select>
            <span class="fts-inline-spacer-5"></span>
          </template>
          <!-- event type:state -->
          <template v-if="eventType === 'state'">
            <span class="fts-inline-spacer-5"></span>
            <fts-state :state="getState" class="event-type-state" />
            <span class="fts-inline-spacer-5"></span>
          </template>
        </div>
      </template>
      <!-- description -->
      <template #description>
        <!-- schedule: digest -->
        <template v-if="scheduleDigest">
          <span class="design-modal-header-tag">Digest</span>
          <span class="design-modal-header-description">one notification listing the matching entries</span>
        </template>
        <!-- notification -->
        <template v-if="formData.actionType === 'notification' && !scheduleDigest">
          <span class="design-modal-header-tag">Notification</span>
          <span class="design-modal-header-description">send a notification, reminder or alert</span>
        </template>
        <!-- advanced -->
        <template v-if="formData.actionType === 'advanced' && !scheduleDigest">
          <span class="design-modal-header-tag">Advanced</span>
          <span class="design-modal-header-description">advanced script (beta)</span>
        </template>
        <!-- automation -->
        <template v-if="formData.actionType === 'automation' && !scheduleDigest">
          <span class="design-modal-header-tag">Automation</span>
          <span class="design-modal-header-description">automation blocks</span>
        </template>
        <!-- event badge -->
        <div class="event-badges" v-if="eventBadges">
          <fts-badge
            v-for="x in eventBadges"
            :key="`event-badge-${x.name}`"
            variant="pill"
            size="sm"
            :tooltip="x.description"
          >{{x.name}}</fts-badge>
        </div>
      </template>
      <!-- actions -->
      <template #actions>
        <fts-toggle-switch
          :form-data="formData"
          :item="{ fieldName: 'enabled' }"
          mat-class
          :size="15"
          label-dir="left"
          class="designer-header-action"
        >
          <div slot="label" class="event-toggle-label">Active</div>
        </fts-toggle-switch>
      </template>
      <!-- body -->
      <template #body>
        <!-- description (a schedule has a name instead, in its designer) -->
        <div class="fts-form-group" v-if="!isSchedule">
          <fts-contenteditable
            v-model="formData.description"
            text-wrap
            placeholder="enter description (optional)"
          />
        </div>
        <!-- converted by an administrator: it keeps running until switched off; its schedule then takes over -->
        <div class="event-schedule-hint" v-if="replacingSchedule" data-replaced-by-schedule>
          <font-awesome-icon :icon="['fas', 'info-circle']" />
          <span v-if="replacingSchedule.enabled !== false">
            Converted to the schedule “{{ replacingSchedule.name || "Schedule" }}”. This automation keeps running as
            before, and the schedule waits. When you switch this automation off, the schedule takes over from its
            next run, so no entry is reminded twice.
          </span>
          <span v-else>
            Converted to the schedule “{{ replacingSchedule.name || "Schedule" }}”, which is switched off. This
            automation keeps running until you switch it off.
          </span>
        </div>
        <!-- timed automations only reach entries saved after they were set up -->
        <div class="event-schedule-hint" v-if="legacyTimingHint" data-legacy-timing-hint>
          <font-awesome-icon :icon="['fas', 'info-circle']" />
          Timed automations only reach entries saved after the automation was set up or changed. To act on every
          matching entry, add a Schedule from the module card instead.
        </div>

        <template v-if="pageAvailability">
          <!-- schedule: when / find / then / delivery -->
          <fts-schedule-designer
            ref="scheduleDesigner"
            :form-data="formData"
            :vector-id="vectorId"
            :states="states"
            :information-source="informationSource"
            :modules="workspaceModules"
            :channels="channels"
            :beta="beta"
            @action-type="setScheduleActionType"
            v-if="isSchedule"
          />
          <!-- notification -->
          <div class="fts-row gutters" v-if="formData.actionType === 'notification' && !scheduleDigest">
            <div class="fts-col fts-col-md-4">
              <fts-form-element
                :form-data="formData"
                :item="{
                  fieldName: 'channel',
                  icon: 'fts-icon-menu-caret-down',
                }"
                comfort-design
              >
                <fts-toggle-tokens
                  slot="formElement"
                  slot-scope="{ formData, item }"
                  v-model="formData[item.fieldName]"
                  :options="channels"
                  comfort-design
                  class="event-channel-container"
                  @selected="preview"
                >
                  <template #optionLabel="{ option, optionLabel }">
                    <font-awesome-icon
                      :icon="option.icon"
                      class="event-channel-icon"
                      v-if="option.icon"
                    />
                    {{ optionLabel }}
                    <template v-if="option.beta">
                      <span class="fts-inline-spacer-1"></span>
                      <fts-release-badge class="fts-align-top" />
                    </template>
                    <template v-if="option.internal">
                      <span class="fts-inline-spacer-1"></span>
                      <fts-release-badge alpha class="fts-align-top" />
                    </template>
                  </template>
                </fts-toggle-tokens>
              </fts-form-element>
            </div>
            <!-- to -->
            <div class="fts-col fts-col-md-8">
              <fts-form-element
                :form-data="formData"
                :item="{
                  label: 'To',
                  fieldName: 'recipients',
                  icon: 'fts-icon-menu-caret-down',
                }"
                comfort-design
              >
                <fts-select
                  slot="formElement"
                  slot-scope="{ formData, item }"
                  :form-data="formData"
                  :item="item"
                  :filterable="false"
                  :options="recipientOptions"
                  :reduce="(x) => x"
                  multiple
                  taggable
                  :additional-filter-keys="['identity']"
                  :selectable="customRecipientSelectable"
                  :create-option="customRecipient"
                  :search-handler="loadRecipients"
                  comfort-design
                  infinite-scroll
                >
                  <template #selected-option-container="{ option }">
                    <!-- member -->
                    <fts-identity-display
                      identity-type="user"
                      :identity-name="option.name"
                      :identity-username="option.identity"
                      :profile-photo-size="16"
                      v-if="option.recipientType === 0"
                    />
                    <!-- user profile -->
                    <fts-identity-display
                      :modules="workspaceModules"
                      identity-type="profile"
                      :identity-id="option.id"
                      :identity-name="option.name"
                      :emoji-size="16"
                      v-else-if="option.recipientType === 7"
                    />
                    <!-- custom -->
                    <fts-identity-display
                      identity-type="custom"
                      :identity-name="option.name"
                      v-else-if="option.recipientType === 8"
                    />
                    <!-- information -->
                    <fts-identity-display
                      :information="informationWithSystem"
                      identity-type="information"
                      :identity-id="option.id"
                      :identity-name="option.name"
                      v-else
                    />
                  </template>
                  <template #option="{ option }">
                    <!-- member -->
                    <fts-identity-display
                      identity-type="user"
                      :identity-name="option.name"
                      :identity-username="option.identity"
                      :profile-photo-size="16"
                      v-if="option.recipientType === 0"
                    />
                    <!-- user profile -->
                    <fts-identity-display
                      :modules="workspaceModules"
                      identity-type="profile"
                      :identity-id="option.id"
                      :identity-name="option.name"
                      :emoji-size="16"
                      v-else-if="option.recipientType === 7"
                    />
                    <!-- custom -->
                    <template v-else-if="option.recipientType === 8">
                      <fts-identity-display identity-type="custom" :identity-name="option.name" />
                      <div
                        class="recipient-option-error"
                        data-option="error"
                        v-if="customRecipientError(option)"
                      >Invalid email address.</div>
                    </template>
                    <!-- information -->
                    <fts-identity-display
                      :information="informationWithSystem"
                      identity-type="information"
                      :identity-id="option.id"
                      :identity-name="option.name"
                      v-else
                    />
                  </template>
                </fts-select>
              </fts-form-element>
              <div class="design-modal-form-group" v-if="beta">
                <fts-toggle-switch
                  :form-data="formData"
                  :item="{ fieldName: 'onBehalf' }"
                  mat-class
                  :size="15"
                  class="design-modal-toggle-switch"
                >
                  <div slot="label" class="design-modal-toggle-switch__label">On Behalf <fts-release-badge class="fts-align-top" /></div>
                </fts-toggle-switch>
              </div>
            </div>
            <!-- message -->
            <div class="fts-col design-modal-form-group">
              <fts-toggle-switch
                :form-data="formData"
                :item="{ fieldName: 'message' }"
                mat-class
                :size="15"
                class="design-modal-toggle-switch"
              >
                <div slot="label" class="design-modal-toggle-switch__label">Message (optional)</div>
              </fts-toggle-switch>

              <fts-transition-expand>
                <div class="design-modal-card design-modal-card-mt" v-if="formData.message">
                  <div class="fts-row gutters">
                    <!-- subject -->
                    <div
                      class="fts-col"
                      v-if="
                        formData.channel != 'whatsapp' &&
                        formData.channel != 'sms'
                      "
                    >
                      <fts-form-element
                        :form-data="formData"
                        :item="{
                          type: 32,
                          displayName: 'Subject',
                          fieldName: 'subjectMessage',
                        }"
                        comfort-design
                      >
                        <fts-formula
                          slot="formElement"
                          slot-scope="{ formData, item }"
                          :variables="variables"
                          v-model="formData[item.fieldName]"
                        />
                      </fts-form-element>
                    </div>
                    <!-- message -->
                    <div class="fts-col">
                      <fts-form-element
                        :form-data="formData"
                        :item="{
                          type: 32,
                          displayName: 'Message',
                          fieldName: 'messageTemplate',
                        }"
                        comfort-design
                      >
                        <fts-formula
                          slot="formElement"
                          :template="true"
                          slot-scope="{ formData, item }"
                          :variables="variables"
                          v-model="formData[item.fieldName]"
                        />
                      </fts-form-element>
                    </div>
                    <!-- email attachment -->
                    <div
                      class="fts-col fts-col-md-4 design-modal-form-group"
                      v-if="formData.channel === 'email'"
                    >
                      <div class="design-modal-form-label">Attachments</div>
                      <div
                        class="event-attachment"
                        v-for="attachment in formData.attachments"
                        :key="attachment.id"
                      >
                        <!-- attachment:select -->
                        <fts-select
                          :form-data="attachment"
                          :item="{ fieldName: 'templateId' }"
                          :options="entryTemplates"
                          comfort-design
                          class="event-attachment-selection"
                        />
                        <!-- attachment:cog -->
                        <fts-btn icon text @click="openAttachmentModal(attachment)">
                          <font-awesome-icon :icon="['fas', 'cog']" />
                        </fts-btn>
                        <!-- attachment:remove -->
                        <fts-btn icon text @click="removeAttachment(attachment)">
                          <font-awesome-icon :icon="['far', 'trash-alt']" />
                        </fts-btn>
                      </div>
                      <!-- attachment:add -->
                      <fts-btn
                        sm
                        outline
                        color="primary"
                        text-transform-init
                        @click="addAttachment"
                      >
                        <font-awesome-icon :icon="['fas', 'plus']" />Add
                        Attachment
                      </fts-btn>
                    </div>
                  </div>
                </div>
              </fts-transition-expand>
            </div>
            <!-- attach link -->
            <div class="fts-col design-modal-form-group">
              <fts-toggle-switch
                :form-data="formData"
                :item="{ fieldName: 'viewLink' }"
                mat-class
                :size="15"
                class="design-modal-toggle-switch"
              >
                <div slot="label" class="design-modal-toggle-switch__label">Attach Link</div>
              </fts-toggle-switch>

              <fts-transition-expand>
                <div class="design-modal-card design-modal-card-mt" v-if="formData.viewLink">
                  <div class="fts-row gutters">
                    <!-- activity -->
                    <div class="fts-col">
                      <!-- activity -->
                      <fts-form-element
                        :form-data="formData"
                        :item="{
                          fieldName: 'activityId',
                          displayName: 'Activity',
                          icon: 'fts-icon-menu-caret-down',
                        }"
                        comfort-design
                        disable-validator-error
                      >
                        <fts-select
                          slot="formElement"
                          slot-scope="{ formData, item, onInput, onBlur }"
                          :form-data="formData"
                          :item="item"
                          :options="shareActivities"
                          comfort-design
                          @input="onInput"
                          @search:blur="onBlur"
                        >
                          <fts-label-group
                            slot="selected-option-container"
                            slot-scope="{ option }"
                            :item-id="option.id"
                            :list="shareActivities"
                            emoji
                          />
                          <fts-label-group
                            slot="option"
                            slot-scope="{ option }"
                            :item-id="option.id"
                            :list="shareActivities"
                            emoji
                          />
                        </fts-select>
                      </fts-form-element>
                    </div>
                    <!-- public -->
                    <div class="fts-col design-modal-form-group">
                      <fts-toggle-switch
                        :form-data="formData"
                        :item="{ fieldName: 'publicLink' }"
                        mat-class
                        :size="15"
                        class="design-modal-toggle-switch"
                      >
                        <div slot="label" class="design-modal-toggle-switch__label">Public (share)</div>
                      </fts-toggle-switch>
                    </div>
                    <!-- comment -->
                    <div class="fts-col design-modal-form-group">
                      <fts-toggle-switch
                        :form-data="formData"
                        :item="{ fieldName: 'disableComment' }"
                        mat-class
                        :size="15"
                        class="design-modal-toggle-switch"
                      >
                        <div slot="label" class="design-modal-toggle-switch__label">Disable comment</div>
                      </fts-toggle-switch>
                    </div>
                    <!-- button label-->
                    <div class="fts-col">
                      <fts-form-element
                        :form-data="formData"
                        :item="{
                          type: 32,
                          displayName: 'Label (optional)',
                          fieldName: 'linkLabel',
                        }"
                        comfort-design
                      >
                        <fts-formula
                          slot="formElement"
                          slot-scope="{ formData, item }"
                          :variables="variables"
                          v-model="formData[item.fieldName]"
                        />
                      </fts-form-element>
                    </div>
                    <!-- listing -->
                    <div class="fts-col" v-if="beta && menus.length">
                      <fts-form-element
                        :form-data="formData"
                        :item="{
                          fieldName: 'listingId',
                          displayName: 'Listing',
                          icon: 'fts-icon-menu-caret-down',
                        }"
                        comfort-design
                        disable-validator-error
                      >
                        <template #formLabel="{ label }">
                          {{label}} <fts-release-badge class="fts-align-top" />
                        </template>
                        <fts-select
                          slot="formElement"
                          slot-scope="{ formData, item, onInput, onBlur }"
                          :form-data="formData"
                          :item="item"
                          :options="menus"
                          comfort-design
                          @input="onInput"
                          @search:blur="onBlur"
                        >
                          <fts-label-group
                            slot="selected-option-container"
                            slot-scope="{ option }"
                            :item-id="option.id"
                            :list="menus"
                            emoji
                          />
                          <fts-label-group
                            slot="option"
                            slot-scope="{ option }"
                            :item-id="option.id"
                            :list="menus"
                            emoji
                          />
                        </fts-select>
                      </fts-form-element>
                    </div>
                  </div>
                </div>
              </fts-transition-expand>
            </div>
          </div>
          <!-- automation -->
          <div class="design-modal-form-group" v-if="formData.actionType === 'automation' && !scheduleDigest">
            <div class="event-automation-selection">
              <!-- a schedule has no transition to run before -->
              <div
                class="automation-design-toggle"
                :class="{
                  'automation-design-toggle--active':
                    automationType === 'before',
                }"
                @click.self="automationType = 'before'"
                v-if="!isSchedule"
              >
                Do before
                <fts-btn
                  text
                  icon
                  class="automation-design-toggle__action"
                  @click="deleteAutomation('automationBefore')"
                  v-if="Boolean(formData.automationBefore)"
                >
                  <font-awesome-icon :icon="['far', 'trash-alt']" />
                </fts-btn>
              </div>
              <div
                class="automation-design-toggle"
                :class="{
                  'automation-design-toggle--active':
                    automationType === 'after',
                }"
                @click.self="automationType = 'after'"
              >
                {{ isSchedule ? "Blocks, for each entry" : "Do after" }}
                <fts-btn
                  text
                  icon
                  class="automation-design-toggle__action"
                  @click="deleteAutomation('automation')"
                  v-if="Boolean(formData.automation)"
                >
                  <font-awesome-icon :icon="['far', 'trash-alt']" />
                </fts-btn>
              </div>
            </div>
            <fts-automation-designer
              :module-id="moduleId"
              :activity-id="formData.item"
              save-button
              :is-saving="isSaving"
              :data="formData.automation"
              :activity-match-validation="testActivityMatchValidation"
              @save="done(true)"
              :key="automationType"
              v-if="automationType == 'after'"
            />
            <fts-automation-designer
              :module-id="moduleId"
              :activity-id="formData.item"
              save-button
              :is-saving="isSaving"
              :data="formData.automationBefore"
              :activity-match-validation="testActivityMatchValidation"
              @save="done(true)"
              :key="automationType"
              v-else-if="automationType == 'before'"
            />
          </div>
          <!-- action type (a schedule picks its action in its own "Then" section) -->
          <div class="fts-row gutters" v-if="formData.actionType !== 'notification' && beta && !isSchedule">
            <div class="fts-col fts-col-md-4 design-modal-form-group">
              <fts-toggle-switch
                v-model="actionTypeAdvanced"
                mat-class
                :size="15"
                class="design-modal-toggle-switch"
              >
                <div slot="label" class="design-modal-toggle-switch__label">
                  Enable Advanced&nbsp;
                  <fts-release-badge class="fts-float-right" />
                </div>
              </fts-toggle-switch>
            </div>
          </div>
          <!-- advanced -->
          <div v-if="formData.actionType === 'advanced' && beta && !scheduleDigest">
            <!-- before script -->
            <div class="design-modal-form-group">
              <fts-script-editor
                editor-id="beforeScript"
                save-button
                :is-saving="isSaving"
                v-model="formData.beforeScript"
                lang="python"
                label="Trigger Before Script"
                @save="done(true)"
                @fullview-change="onAdvancedFullViewChange('beforeScript', $event)"
              >
                <template #label="{ label }">
                  {{ label }} <fts-release-badge class="fts-align-top" />
                </template>
              </fts-script-editor>
            </div>
            <!-- debug output (internal or beta users) -->
            <div class="design-modal-form-group" v-if="isInternal || beta">
              <fts-debug-panel
                ref="advancedDebugPanel"
                :state-key="advancedActiveFullViewEditor || 'advancedDebugPanel'"
                :inline="!advancedAnyFullView"
                :floating="advancedAnyFullView"
                test-button
                :is-testing="previewSending"
                @test="testAutomation"
              />
            </div>
            <!-- after script -->
            <div class="design-modal-form-group">
              <fts-script-editor
                editor-id="afterScript"
                save-button
                :is-saving="isSaving"
                v-model="formData.afterScript"
                lang="python"
                label="Trigger After Script"
                @save="done(true)"
                @fullview-change="onAdvancedFullViewChange('afterScript', $event)"
              >
                <template #label="{ label }">
                  {{ label }} <fts-release-badge class="fts-align-top" />
                </template>
              </fts-script-editor>
            </div>
          </div>
          <!-- notification:test -->
          <div
            class="event-test design-modal-form-group"
            v-if="
              (formData.actionType === 'notification' ||
              formData.actionType === 'advanced') && !scheduleDigest
            "
          >
            <div class="event-test-legend">
              Test
              {{
              formData.actionType === "notification"
              ? "notification"
              : "automation"
              }}
              with:
            </div>
            <div class="fts-row gutters">
              <!-- entry -->
              <div class="fts-col fts-col-md-4">
                <fts-form-element
                  :form-data="formData"
                  :item="{
                    displayName: 'Entry',
                    key: 'testLocusId',
                    fieldName: 'testLocus',
                    connectionId: vectorId,
                    type: 7,
                  }"
                  comfort-design
                  class="event-test-selector"
                >
                  <fts-dropdown
                    slot="formElement"
                    slot-scope="{ formData, item }"
                    :form-data="formData"
                    :item="item"
                    :module-design="vectorId.toString()"
                    comfort-design
                    @input="testLocusDetailChanged"
                  />
                </fts-form-element>
              </div>
              <!-- activity -->
              <div class="fts-col fts-col-md-4" v-if="formData.testLocusId">
                <fts-form-element
                  :form-data="formData"
                  :item="{
                    displayName: 'Activity',
                    key: 'testLocusDetailId',
                    fieldName: 'testLocusDetail',
                    connectionId: vectorId,
                    type: 7,
                  }"
                  comfort-design
                  class="event-test-selector"
                >
                  <fts-dropdown
                    slot="formElement"
                    slot-scope="{ formData, item }"
                    :form-data="formData"
                    :item="item"
                    :module-design="vectorId.toString()"
                    :locus-id="formData.testLocusId"
                    locus-detail
                    comfort-design
                  >
                    <fts-label-group
                      slot="option"
                      slot-scope="{ option }"
                      fontawesome
                      :item="{
                        icon: testActivityMatchValidation(formData.item, option.activityId) ? null : ['fas', 'exclamation-circle'],
                        name: option.optionName
                      }"
                    />
                  </fts-dropdown>
                </fts-form-element>
              </div>
              <!-- action -->
              <div class="fts-col">
                <div class="event-test-action">
                  <fts-btn
                    sm
                    outline
                    text-transform-init
                    :loading="previewLoading"
                    @click="preview"
                    v-if="formData.actionType === 'notification'"
                  >Preview</fts-btn>
                  <fts-btn
                    sm
                    outline
                    text-transform-init
                    :loading="previewSending"
                    @click="testAutomation"
                  >
                    Test
                    {{
                    formData.actionType === "notification" ? "Send" : "Script"
                    }}
                  </fts-btn>
                </div>
                <!-- action helper -->
                <div class="event-test-action-helper" v-if="formData.actionType === 'notification'">
                  By clicking
                  <b>Test Send</b> only you will receive this notification.
                </div>
              </div>
            </div>
            <!-- preview -->
            <div class="fts-col event-preview" v-if="notificationPreview">
              <div class="event-preview-result">
                <div class="event-preview-wrapper">
                  <!-- subject -->
                  <div class="event-preview-subject-wrapper">
                    <fts-badge color="text" class="event-preview-subject-badge">
                      Subject
                    </fts-badge>
                    <span class="event-preview-subject">
                      {{notificationPreview.subject}}
                    </span>
                  </div>
                  <!-- recipient -->
                  <div
                    class="event-preview-recipient-container"
                    v-if="
                      notificationPreview &&
                      notificationPreview.recipients &&
                      notificationPreview.recipients.length
                    "
                  >
                    <div>To:</div>
                    <template v-for="recipient in notificationPreview.recipients">
                      <fts-profile
                        :key="recipient.to"
                        :user-name="recipient.to"
                        class="event-preview-recipient"
                      >
                        <template #profileName="{ userProfileName }">
                          <fts-tippy :content="recipient.to" class="event-preview-recipient-tippy">
                            <div slot="trigger">{{ userProfileName || recipient }}</div>
                          </fts-tippy>
                        </template>
                      </fts-profile>
                    </template>
                  </div>
                </div>
                <!-- content -->
                <div class="event-preview-content">
                  <iframe class="event-preview-iframe" ref="notificationContentIframe" />
                </div>
              </div>
            </div>
          </div>
          <!-- recurring (a schedule's recurrence is its "When to run") -->
          <div class="design-modal-form-group" v-if="!isSchedule">
            <fts-toggle-switch
              :form-data="formData"
              :item="{ fieldName: 'repeat' }"
              mat-class
              :size="15"
              class="design-modal-toggle-switch"
            >
              <div slot="label" class="design-modal-toggle-switch__label">Recurring</div>
            </fts-toggle-switch>
            <fts-transition-expand>
              <div class="design-modal-card design-modal-card-mt" v-if="formData.repeat">
                <div class="fts-row gutters">
                  <!-- starts on -->
                  <div class="fts-col fts-col-md-4">
                    <fts-form-element
                      :form-data="formData"
                      :item="{
                        fieldName: 'startsOn',
                        type: 6,
                        minDatetime: moment().startOf('day').format(),
                      }"
                      comfort-design
                    />
                  </div>
                  <!-- recurring type -->
                  <div class="fts-col fts-col-md-4">
                    <fts-form-element
                      :form-data="formData"
                      :item="{
                        fieldName: 'recurringType',
                        icon: 'fts-icon-menu-caret-down',
                      }"
                      comfort-design
                    >
                      <fts-select
                        slot="formElement"
                        slot-scope="{ formData, item }"
                        :form-data="formData"
                        :item="item"
                        :options="recurringTypeOptions"
                        comfort-design
                      />
                    </fts-form-element>
                  </div>
                  <!-- every -->
                  <div
                    class="fts-col fts-col-md-4"
                    key="every"
                    v-if="formData.recurringType === 'day'"
                  >
                    <fts-form-element
                      :form-data="formData"
                      :item="{ fieldName: 'every', type: 3 }"
                      comfort-design
                    />
                  </div>
                  <!-- recurring week -->
                  <div class="fts-col" key="recurringWeek" v-if="formData.recurringType === 'week'">
                    <fts-form-element
                      :form-data="formData"
                      :item="{
                        fieldName: 'recurringWeeks',
                        icon: 'fts-icon-check-square',
                      }"
                      comfort-design
                    >
                      <fts-toggle-tokens
                        slot="formElement"
                        slot-scope="{ formData, item }"
                        v-model="formData[item.fieldName]"
                        :options="weeks"
                        multiple
                        comfort-design
                      />
                    </fts-form-element>
                  </div>
                  <!-- recurring month -->
                  <div
                    class="fts-col"
                    key="recurringMonth"
                    v-if="formData.recurringType === 'month'"
                  >
                    <div class="design-modal-form-group">
                      <div class="design-modal-form-label">Recurring:</div>
                      <div class="event-checklist comfort-form-design">
                        <div class="event-checklist-item">
                          <checkbox
                            id="recurringMonthStart"
                            v-model="formData.recurringMonthStart"
                            color="var(--fts-primary)"
                            :size="18"
                            class="event-checkbox"
                          />
                          <label
                            for="recurringMonthStart"
                            class="event-checklist-item-label"
                          >Start of the month</label>
                        </div>

                        <div class="event-checklist-item">
                          <checkbox
                            id="recurringMonthDay"
                            v-model="recurringMonthDay"
                            color="var(--fts-primary)"
                            :size="18"
                            class="event-checkbox"
                          />
                          <label for="recurringMonthDay" class="event-checklist-item-label">Every</label>
                          <span class="fts-inline-spacer-5"></span>
                          <fts-integer
                            v-model="recurringMonthDayEvery"
                            :max="31"
                            :min="1"
                            inline
                            class="event-checklist-integer"
                            ref="eventChecklistInteger"
                          />
                          <span class="fts-inline-spacer-5"></span>
                          <label
                            for="recurringMonthDay"
                            class="event-checklist-item-label"
                          >of the month</label>
                        </div>

                        <div class="event-checklist-item">
                          <checkbox
                            id="recurringMonthEnd"
                            v-model="formData.recurringMonthEnd"
                            color="var(--fts-primary)"
                            :size="18"
                            class="event-checkbox"
                          />
                          <label
                            for="recurringMonthEnd"
                            class="event-checklist-item-label"
                          >End of the month</label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </fts-transition-expand>
          </div>
          <!-- advanced settings -->
          <fts-collapsible-element v-model="advanced" v-if="!scheduleDigest">
            <fts-btn
              slot="toggle"
              slot-scope="{ isVisible, toggle }"
              text
              text-transform-init
              :class="{ open: isVisible }"
              class="design-modal-advanced-toggle"
              @click="toggle()"
            >
              Advanced settings
              <font-awesome-icon
                :icon="['fas', 'caret-right']"
                class="design-modal-advanced-toggle-icon"
              />
            </fts-btn>
            <div slot="content" class="design-modal-advanced-content fts-row gutters">
              <!-- cc -->
              <div class="fts-col" v-if="formData.channel == 'email'">
                <fts-form-element
                  :form-data="formData"
                  :item="{
                    label: 'CC',
                    fieldName: 'cc',
                    icon: 'fts-icon-menu-caret-down',
                  }"
                  comfort-design
                >
                  <fts-select
                    slot="formElement"
                    slot-scope="{ formData, item }"
                    :form-data="formData"
                    :item="item"
                    :filterable="false"
                    :options="recipientOptions"
                    :reduce="(x) => x"
                    multiple
                    taggable
                    :additional-filter-keys="['identity']"
                    :selectable="customRecipientSelectable"
                    :create-option="customRecipient"
                    :search-handler="loadRecipients"
                    comfort-design
                    infinite-scroll
                  >
                    <template #selected-option-container="{ option }">
                      <!-- member -->
                      <fts-identity-display
                        identity-type="user"
                        :identity-name="option.name"
                        :identity-username="option.identity"
                        :profile-photo-size="16"
                        v-if="option.recipientType === 0"
                      />
                      <!-- user profile -->
                      <fts-identity-display
                        :modules="workspaceModules"
                        identity-type="profile"
                        :identity-id="option.id"
                        :identity-name="option.name"
                        :emoji-size="16"
                        v-else-if="option.recipientType === 7"
                      />
                      <!-- custom -->
                      <fts-identity-display
                        identity-type="custom"
                        :identity-name="option.name"
                        v-else-if="option.recipientType === 8"
                      />
                      <!-- information -->
                      <fts-identity-display
                        :information="informationWithSystem"
                        identity-type="information"
                        :identity-id="option.id"
                        :identity-name="option.name"
                        v-else
                      />
                    </template>
                    <template #option="{ option }">
                      <!-- member -->
                      <fts-identity-display
                        identity-type="user"
                        :identity-name="option.name"
                        :identity-username="option.identity"
                        :profile-photo-size="16"
                        v-if="option.recipientType === 0"
                      />
                      <!-- user profile -->
                      <fts-identity-display
                        :modules="workspaceModules"
                        identity-type="profile"
                        :identity-id="option.id"
                        :identity-name="option.name"
                        :emoji-size="16"
                        v-else-if="option.recipientType === 7"
                      />
                      <!-- custom -->
                      <template v-else-if="option.recipientType === 8">
                        <fts-identity-display identity-type="custom" :identity-name="option.name" />
                        <div
                          class="recipient-option-error"
                          data-option="error"
                          v-if="customRecipientError(option)"
                        >Invalid email address.</div>
                      </template>
                      <!-- information -->
                      <fts-identity-display
                        :information="informationWithSystem"
                        identity-type="information"
                        :identity-id="option.id"
                        :identity-name="option.name"
                        v-else
                      />
                    </template>
                  </fts-select>
                </fts-form-element>
              </div>
              <!-- reply to -->
              <div class="fts-col" v-if="formData.channel == 'email'">
                <fts-form-element
                  :form-data="formData"
                  :item="{
                    label: 'Reply To',
                    fieldName: 'replyTo',
                    icon: 'fts-icon-menu-caret-down',
                  }"
                  comfort-design
                >
                  <fts-select
                    slot="formElement"
                    slot-scope="{ formData, item }"
                    :form-data="formData"
                    :item="item"
                    :filterable="false"
                    :options="recipientOptions"
                    :reduce="(x) => x"
                    multiple
                    taggable
                    :additional-filter-keys="['identity']"
                    :selectable="customRecipientSelectable"
                    :create-option="customRecipient"
                    :search-handler="loadRecipients"
                    comfort-design
                    infinite-scroll
                  >
                    <template #selected-option-container="{ option }">
                      <!-- member -->
                      <fts-identity-display
                        identity-type="user"
                        :identity-name="option.name"
                        :identity-username="option.identity"
                        :profile-photo-size="16"
                        v-if="option.recipientType === 0"
                      />
                      <!-- user profile -->
                      <fts-identity-display
                        :modules="workspaceModules"
                        identity-type="profile"
                        :identity-id="option.id"
                        :identity-name="option.name"
                        :emoji-size="16"
                        v-else-if="option.recipientType === 7"
                      />
                      <!-- custom -->
                      <fts-identity-display
                        identity-type="custom"
                        :identity-name="option.name"
                        v-else-if="option.recipientType === 8"
                      />
                      <!-- information -->
                      <fts-identity-display
                        :information="informationWithSystem"
                        identity-type="information"
                        :identity-id="option.id"
                        :identity-name="option.name"
                        v-else
                      />
                    </template>
                    <template #option="{ option }">
                      <!-- member -->
                      <fts-identity-display
                        identity-type="user"
                        :identity-name="option.name"
                        :identity-username="option.identity"
                        :profile-photo-size="16"
                        v-if="option.recipientType === 0"
                      />
                      <!-- user profile -->
                      <fts-identity-display
                        :modules="workspaceModules"
                        identity-type="profile"
                        :identity-id="option.id"
                        :identity-name="option.name"
                        :emoji-size="16"
                        v-else-if="option.recipientType === 7"
                      />
                      <!-- custom -->
                      <template v-else-if="option.recipientType === 8">
                        <fts-identity-display identity-type="custom" :identity-name="option.name" />
                        <div
                          class="recipient-option-error"
                          data-option="error"
                          v-if="customRecipientError(option)"
                        >Invalid email address.</div>
                      </template>
                      <!-- information -->
                      <fts-identity-display
                        :information="informationWithSystem"
                        identity-type="information"
                        :identity-id="option.id"
                        :identity-name="option.name"
                        v-else
                      />
                    </template>
                  </fts-select>
                </fts-form-element>
              </div>
              <!-- recipient script -->
              <div class="fts-col design-modal-form-group" v-if="beta">
                <fts-toggle-switch
                  :form-data="formData"
                  :item="{ fieldName: 'enableRecipientScript' }"
                  mat-class
                  :size="15"
                  class="design-modal-toggle-switch"
                >
                  <div slot="label" class="design-modal-toggle-switch__label">
                    Recipient Script&nbsp;
                    <fts-release-badge class="fts-float-right" />
                  </div>
                </fts-toggle-switch>
                <fts-transition-expand>
                  <div
                    class="design-modal-card design-modal-card-mt design-modal-card-pb"
                    v-if="formData.enableRecipientScript"
                  >
                    <fts-script-editor
                      editor-id="recipientScript"
                      save-button
                      :is-saving="isSaving"
                      v-model="formData.recipientScript"
                      lang="python"
                      label="Recipient Script"
                      ref="recipientScript"
                      @save="done(true)"
                    />
                  </div>
                </fts-transition-expand>
              </div>
              <!-- customize message -->
              <div
                class="fts-col design-modal-form-group"
                v-if="formData.actionType === 'notification'"
              >
                <fts-toggle-switch
                  :form-data="formData"
                  :item="{ fieldName: 'customizeMessage' }"
                  mat-class
                  :size="15"
                  class="design-modal-toggle-switch"
                >
                  <div slot="label" class="design-modal-toggle-switch__label">Customize Message</div>
                </fts-toggle-switch>
                <fts-transition-expand>
                  <div
                    class="design-modal-card design-modal-card-mt"
                    ref="customizeMessage"
                    v-if="formData.customizeMessage"
                  >
                    <div class="fts-row gutters">
                      <!-- subject -->
                      <div
                        class="fts-col"
                        v-if="
                          formData.channel != 'whatsapp' &&
                          formData.channel != 'sms'
                        "
                      >
                        <fts-form-element
                          :form-data="formData"
                          :item="{
                            type: 32,
                            displayName: 'Subject',
                            fieldName: 'subjectTemplate',
                          }"
                          comfort-design
                        >
                          <fts-template-editor
                            slot="formElement"
                            slot-scope="{ formData, item }"
                            :type="'text'"
                            :information="information"
                            v-model="formData[item.fieldName]"
                          />
                        </fts-form-element>
                      </div>
                      <!-- content -->
                      <div class="fts-col">
                        <fts-form-element
                          :form-data="formData"
                          :item="{
                            type: 32,
                            displayName: 'Content',
                            fieldName: 'template',
                          }"
                          comfort-design
                        >
                          <fts-template-editor
                            slot="formElement"
                            slot-scope="{ formData, item }"
                            :information="information"
                            v-model="formData[item.fieldName]"
                          />
                        </fts-form-element>
                      </div>
                      <!-- email attachment -->
                      <div
                        class="fts-col fts-col-md-4 design-modal-form-group"
                        v-if="formData.channel === 'email'"
                      >
                        <div class="design-modal-form-label">Attachments</div>
                        <div
                          class="event-attachment"
                          v-for="attachment in formData.attachments"
                          :key="attachment.id"
                        >
                          <!-- attachment:select -->
                          <fts-select
                            :form-data="attachment"
                            :item="{ fieldName: 'templateId' }"
                            :options="entryTemplates"
                            comfort-design
                            class="event-attachment-selection"
                          />
                          <!-- attachment:cog -->
                          <fts-btn icon text @click="openAttachmentModal(attachment)">
                            <font-awesome-icon :icon="['fas', 'cog']" />
                          </fts-btn>
                          <!-- attachment:remove -->
                          <fts-btn icon text @click="removeAttachment(attachment)">
                            <font-awesome-icon :icon="['far', 'trash-alt']" />
                          </fts-btn>
                        </div>
                        <!-- attachment:add -->
                        <fts-btn
                          sm
                          outline
                          color="primary"
                          text-transform-init
                          @click="addAttachment"
                        >
                          <font-awesome-icon :icon="['fas', 'plus']" />Add
                          Attachment
                        </fts-btn>
                      </div>
                      <!-- template -->
                      <div class="fts-col design-modal-form-group" v-if="beta">
                        <fts-script-editor
                          editor-id="messageTemplate"
                          save-button
                          :is-saving="isSaving"
                          v-model="formData.template"
                          lang="python"
                          label="Message Template"
                          ref="template"
                          @save="done(true)"
                        >
                          <template #label="{ label }">
                            {{ label }} <fts-release-badge class="fts-align-top" />
                          </template>
                        </fts-script-editor>
                      </div>
                    </div>
                  </div>
                </fts-transition-expand>
              </div>
              <!-- general (per trigger only) -->
              <div class="fts-col design-modal-form-group" v-if="!isSchedule">
                <fts-toggle-switch
                  :form-data="formData"
                  :item="{ fieldName: 'general' }"
                  mat-class
                  :size="15"
                  class="design-modal-toggle-switch"
                >
                  <div slot="label" class="design-modal-toggle-switch__label">
                    Execute exactly one time for each trigger without iterate
                    through entries
                  </div>
                </fts-toggle-switch>
              </div>
              <!-- condition (a schedule's condition is its native filter only: no scripts) -->
              <div class="fts-col design-modal-form-group" v-if="!isSchedule">
                <fts-toggle-switch
                  :form-data="formData"
                  :item="{ fieldName: 'condition' }"
                  mat-class
                  :size="15"
                  class="design-modal-toggle-switch"
                >
                  <div slot="label" class="design-modal-toggle-switch__label">Condition</div>
                </fts-toggle-switch>
                <fts-transition-expand>
                  <div class="design-modal-card design-modal-card-mt" v-if="formData.condition">
                    <div class="fts-row gutters">
                      <!-- condition state -->
                      <div class="fts-col fts-col-md-4">
                        <fts-form-element
                          :form-data="formData"
                          :item="{
                            fieldName: 'conditionState',
                            icon: 'fts-icon-menu-caret-down',
                          }"
                          comfort-design
                        >
                          <fts-select
                            slot="formElement"
                            slot-scope="{ formData, item }"
                            :formData="formData"
                            :item="item"
                            :options="states"
                            comfort-design
                          >
                            <fts-state
                              slot="selected-option-container"
                              slot-scope="{ option }"
                              :state="option"
                            />
                            <fts-state slot="option" slot-scope="{ option }" :state="option" />
                          </fts-select>
                        </fts-form-element>
                      </div>
                      <!-- condition script -->
                      <div class="fts-col design-modal-form-group" v-if="beta">
                        <fts-collapsible-element
                          :condition="formData.conditionScript"
                          label="condition script"
                          immersive
                          beta
                        >
                          <fts-script-editor
                            slot="content"
                            editor-id="conditionScript"
                            save-button
                            :is-saving="isSaving"
                            v-model="formData.conditionScript"
                            lang="python"
                            label="Condition Script"
                            ref="conditionScript"
                            @save="done(true)"
                          >
                            <template #label="{ label }">
                              {{label}} <fts-release-badge class="fts-align-top" />
                            </template>
                          </fts-script-editor>
                        </fts-collapsible-element>
                      </div>
                      <!-- query script -->
                      <div class="fts-col design-modal-form-group" v-if="beta">
                        <fts-collapsible-element
                          :condition="formData.queryScript"
                          label="query script"
                          immersive
                          beta
                        >
                          <fts-script-editor
                            slot="content"
                            editor-id="queryScript"
                            save-button
                            :is-saving="isSaving"
                            v-model="formData.queryScript"
                            lang="python"
                            label="Query Script"
                            ref="queryScript"
                            @save="done(true)"
                          >
                            <template #label="{ label }">
                              {{label}} <fts-release-badge class="fts-align-top" />
                            </template>
                          </fts-script-editor>
                        </fts-collapsible-element>
                      </div>
                    </div>
                  </div>
                </fts-transition-expand>
              </div>
              <!-- timing (per trigger only) -->
              <div class="fts-col design-modal-form-group" v-if="beta && !isSchedule">
                <fts-toggle-switch
                  :form-data="formData"
                  :item="{ fieldName: 'timeModifier' }"
                  mat-class
                  :size="15"
                  class="design-modal-toggle-switch"
                  @change="onTimingToggleChange"
                >
                  <div slot="label" class="design-modal-toggle-switch__label">
                    Timing&nbsp;
                    <fts-release-badge class="fts-float-right" />
                  </div>
                </fts-toggle-switch>
                <fts-transition-expand>
                  <div class="design-modal-card design-modal-card-mt" v-if="formData.timeModifier">
                    <div class="fts-row gutters">
                      <!-- before (target date) -->
                      <div class="fts-col fts-col-md-4" v-if="formData.trigger === 'target'">
                        <fts-form-element
                          :form-data="formData"
                          :item="{
                            fieldName: 'before',
                            input: 'toggleSwitch',
                          }"
                          comfort-design
                        />
                      </div>
                      <!-- once -->
                      <div class="fts-col fts-col-md-4" v-if="isInternal && !formData.repeat">
                        <fts-form-element
                          :form-data="formData"
                          :item="{
                            fieldName: 'onceOn',
                            type: 5,
                          }"
                          comfort-design
                        >
                          <template #formLabel="{ label }">
                            {{label}}
                            <fts-release-badge alpha class="fts-align-top" />
                          </template>
                        </fts-form-element>
                      </div>
                      <!-- target time -->
                      <div class="fts-col fts-col-md-4">
                        <fts-form-element
                          :form-data="formData"
                          :item="{
                            fieldName: 'targetTime',
                            type: 6,
                            predefinedFormatId: 't',
                          }"
                          comfort-design
                        />
                      </div>
                      <!-- multiplier -->
                      <div class="fts-col fts-col-md-4">
                        <fts-form-element
                          :form-data="formData"
                          :item="{
                            fieldName: 'multipler',
                            displayName: 'Multiplier',
                            type: 3,
                            decimalPlaces: 0,
                          }"
                          comfort-design
                        />
                      </div>
                      <!-- duration type -->
                      <div class="fts-col fts-col-md-4">
                        <fts-form-element
                          :form-data="formData"
                          :item="{
                            fieldName: 'durationType',
                            icon: 'fts-icon-menu-caret-down',
                          }"
                          comfort-design
                        >
                          <fts-select
                            slot="formElement"
                            slot-scope="{ formData, item }"
                            :formData="formData"
                            :item="item"
                            :options="
                              durationTypeOptions.filter(
                                (x) =>
                                  !x.isInternal ||
                                  (x.isInternal &&
                                    (isInternal ||
                                      x.id === formData.durationType))
                              )
                            "
                            comfort-design
                          >
                            <template #option="{ option }">
                              {{ option.name }}
                              <fts-release-badge
                                alpha
                                class="fts-align-top"
                                v-if="option.isInternal"
                              />
                            </template>
                          </fts-select>
                        </fts-form-element>
                      </div>
                    </div>
                  </div>
                </fts-transition-expand>
              </div>
            </div>
          </fts-collapsible-element>
        </template>
        <!-- unavailable message -->
        <div class="event-unavailable-message" v-else>
          <div class="event-unavailable-message-title">Advanced Setting Enabled</div>
          <div>
            You are not able to access this feature currently as it is in beta release. 
            If you wish to make any changes, please reach out to your administrator for assistance. 
            Alternatively, you can seek support and engage with our community on
            <a :href="discordURL" target="_blank" class="fts-link-primary">Discord</a>, where our support team is available to help.
          </div>
        </div>
      </template>
      <!-- footer:left -->
      <template #footerLeft v-if="pageAvailability">
        <fts-tippy content="Delete" v-if="formData.id">
          <fts-btn slot="trigger" text icon @click="remove">
            <font-awesome-icon :icon="['far', 'trash-alt']" />
          </fts-btn>
        </fts-tippy>

        <fts-tippy content="Duplicate" v-if="formData.id && beta">
          <fts-btn slot="trigger" text @click="duplicate">
            <font-awesome-icon :icon="['far', 'clone']" />
            <fts-release-badge />
          </fts-btn>
        </fts-tippy>
      </template>
      <!-- footer:right -->
      <template #footerRight>
        <fts-btn text @click="beforeClose">Cancel</fts-btn>

        <fts-btn 
          text 
          color="primary" 
          :loading="isSaving" 
          @click="done(true)" 
        >
          <font-awesome-icon :icon="['fas', 'check']" />Save
        </fts-btn>

        <fts-btn outline color="primary" @click="done">
          <font-awesome-icon :icon="['fas', 'check']" />Ok
        </fts-btn>
      </template>
    </fts-design-modal>
    <!-- attachment modal -->
    <fts-design-modal
      :title="`Attachment for ${title}`"
      desc="Notification email attachment designer"
      @closed="closeAttachmentModal"
      ref="attachmentModal"
    >
      <div slot="body" class="compact-form-design">
        <div class="fts-form-group">
          <fts-form-label placement="top">Listing</fts-form-label>
          <fts-select
            :formData="attachmentFormData.configuration"
            :item="{ fieldName: 'menu' }"
            :options="menus"
          >
            <fts-label-group
              slot="selected-option-container"
              slot-scope="{ option }"
              :item-id="option.id"
              :list="menus"
              emoji
            />
            <fts-label-group
              slot="option"
              slot-scope="{ option }"
              :item-id="option.id"
              :list="menus"
              emoji
            />
          </fts-select>
        </div>

        <div class="fts-form-group">
          <fts-form-label placement="top">Profile Only</fts-form-label>
          <fts-toggle-switch
            :formData="attachmentFormData.configuration"
            :item="{ fieldName: 'profileOnly' }"
            :size="18"
            :label="false"
            matClass
          />
        </div>

        <template
          v-if="
            attachmentFormData.configuration &&
            !attachmentFormData.configuration.profileOnly
          "
        >
          <div class="fts-form-group">
            <fts-form-label placement="top">Query Filter</fts-form-label>
            <fts-query-designer
              :modules="workspaceModules"
              :states="states"
              :informationSource="informationSource"
              :filter="attachmentFormData.configuration.filter"
            />
          </div>

          <div class="fts-form-group" v-if="beta">
            <fts-script-editor
              editor-id="queryScript"
              save-button
              :is-saving="isSaving"
              v-model="attachmentFormData.configuration.queryScript"
              lang="python"
              @save="done(true)"
            >
              <fts-form-label slot="label" placement="top">
                Query Script
                <fts-release-badge class="fts-align-top" />
              </fts-form-label>
            </fts-script-editor>
          </div>
        </template>
      </div>
      <template #footer>
        <fts-btn outline color="primary" @click="updateAttachmentFormData(attachmentFormData)">Done</fts-btn>
      </template>
    </fts-design-modal>
  </div>
</template>

<script>
import Vue from "vue";
import changesets from "diff-json";
import moment from "moment";

import { authorizationStore } from "../store/authorizationStore";
import commons from "../commons/commons";
import queryLogic from "../commons/queryLogic";
import { emailValidator } from "../commons/validator";
import { Channel } from "../commons/notification";

import FTSDesignModal from "./FTSDesignModal";
import FTSFormula from "./FTSFormula";
import FTSQueryDesigner from "./FTSQueryDesigner";
import FTSScriptEditor from "./FTSScriptEditor";
import FTSTemplateEditor from "./FTSTemplateEditor.vue";
import FTSAutomationDesigner from "./automation/FTSAutomationDesigner";
import { offerBlockRewrites } from "./automation/blockRewrites";
import FTSBadge from '../elements/FTSBadge';
import FTSBtn from "../elements/FTSBtn";
import FTSCollapsibleElement from "../elements/FTSCollapsibleElement";
import FTSContenteditable from "../elements/FTSContenteditable";
import FTSDropdown from "../elements/FTSDropdown";
import FTSDropdownSelect from "../elements/FTSDropdownSelect";
import FTSFormLabel from "../elements/FTSFormLabel";
import FTSFormElement from "../elements/FTSFormElement";
import FTSIdentityDisplay from "../elements/FTSIdentityDisplay";
import FTSInteger from "../elements/FTSInteger";
import FTSMoji from "../elements/FTSMoji";
import FTSProfile from "../elements/FTSProfile";
import FTSSelect from "../elements/FTSSelect";
import FTSState from "../elements/FTSState";
import FTSToggleSwitch from "../elements/FTSToggleSwitch";
import FTSTransitionExpand from "../elements/FTSTransitionExpand";
import FTSTippy from "../elements/FTSTippy";
import FTSToggleTokens from "../elements/FTSToggleTokens";
import FTSLabelGroup from "../elements/FTSLabelGroup";
import FTSDebugPanel from "../elements/FTSDebugPanel";
import FTSScheduleDesigner from "./schedule/FTSScheduleDesigner";
import {
  describeSchedule,
  isDigest,
  isSchedule,
  normalizeTemplate,
  prepareForSave,
  replacementOf,
  validateTemplate,
} from "./schedule/scheduleLogic";
import Checkbox from "vue-material-checkbox";

const dateTimeOptions = Object.freeze([
  { id: "day", name: "Day" },
  { id: "week", name: "Week" },
  { id: "month", name: "Month" }
]);

const Action_Type = Object.freeze({
  Notification: "notification",
  Automation: "automation",
  Advanced: "advanced"
});

export default {
  components: {
    "fts-design-modal": FTSDesignModal,
    "fts-formula": FTSFormula,
    "fts-query-designer": FTSQueryDesigner,
    "fts-script-editor": FTSScriptEditor,
    "fts-template-editor": FTSTemplateEditor,
    "fts-automation-designer": FTSAutomationDesigner,
    "fts-badge": FTSBadge,
    "fts-btn": FTSBtn,
    "fts-collapsible-element": FTSCollapsibleElement,
    "fts-contenteditable": FTSContenteditable,
    "fts-dropdown": FTSDropdown,
    "fts-dropdown-select": FTSDropdownSelect,
    "fts-form-label": FTSFormLabel,
    "fts-form-element": FTSFormElement,
    "fts-identity-display": FTSIdentityDisplay,
    "fts-integer": FTSInteger,
    "fts-moji": FTSMoji,
    "fts-profile": FTSProfile,
    "fts-select": FTSSelect,
    "fts-state": FTSState,
    "fts-toggle-switch": FTSToggleSwitch,
    "fts-transition-expand": FTSTransitionExpand,
    "fts-tippy": FTSTippy,
    "fts-toggle-tokens": FTSToggleTokens,
    "fts-label-group": FTSLabelGroup,
    "fts-debug-panel": FTSDebugPanel,
    "fts-schedule-designer": FTSScheduleDesigner,
    checkbox: Checkbox
  },
  data: () => ({
    discordURL: commons.discordURL,
    automationType: null,
    formData: {},
    originalData: null,
    resolve: null,
    advanced: false,
    previewLoading: false,
    previewSending: false,
    notificationPreview: null,
    actionTypes: [
      { id: Action_Type.Notification, name: "Notification" },
      { id: Action_Type.Automation, name: "Automation" },
      { id: Action_Type.Advanced, name: "Advanced" }
    ],
    triggers: [
      { id: "change", name: "changed" },
      { id: "target", name: "due" },
      { id: "execute", name: "performing" },
      { id: "changeFrom", name: "changed from" },
      { id: "changeTo", name: "changed to" }
    ],
    recurringTypeOptions: dateTimeOptions,
    durationTypeOptions: [
      { id: "minute", name: "Minute", isInternal: true },
      { id: "hour", name: "Hour", isInternal: true },
      ...dateTimeOptions
    ],
    weeks: [
      { name: "Monday", value: "monday" },
      { name: "Tuesday", value: "tuesday" },
      { name: "Wednesday", value: "wednesday" },
      { name: "Thursday", value: "thursday" },
      { name: "Friday", value: "friday" },
      { name: "Saturday", value: "saturday" },
      { name: "Sunday", value: "sunday" }
    ],
    recipientOptions: [],
    attachmentFormData: {},
    advancedFullViewMap: {}
  }),
  computed: {
    variables() {
      return [
        {
          name: "Entry",
          fieldName: "entry",
          type: 35,
          moduleId: this.$store.state.vectorId.toString()
        }
      ];
    },
    isInternal() {
      return authorizationStore.getters.isInternal;
    },
    /* A Studio schedule (canvas trigger type "routine", docs/scheduling-routine-automation-proposal.md).
       It opens here in schedule mode: its own When / Find / Then / Delivery sections, the same
       per-entry action editors, and none of the per-trigger settings (timing, recurring, condition
       scripts) — which would otherwise be written onto a template the scheduler reads differently. */
    isSchedule() {
      return isSchedule(this.formData);
    },
    scheduleDigest() {
      return this.isSchedule && isDigest(this.formData);
    },
    scheduleSentence() {
      return this.isSchedule ? describeSchedule(this.formData.schedule) : "";
    },
    /* timed legacy automations point at Schedule once schedules are offered (flag appStudioSchedule) */
    legacyTimingHint() {
      return !this.isSchedule &&
        !this.replacingSchedule &&
        Boolean(authorizationStore.getters.appStudioSchedule) &&
        Boolean(this.formData.timeModifier || this.formData.trigger === "target");
    },
    /* a timing automation an administrator converted: its replacement schedule, still in the module */
    replacingSchedule() {
      return replacementOf(this.formData, this.$store.getters.vectorTriggers);
    },
    advancedAnyFullView() {
      return Object.values(this.advancedFullViewMap).some(Boolean);
    },
    advancedActiveFullViewEditor() {
      const pair = Object.entries(this.advancedFullViewMap).find(([, value]) => value);
      return pair && pair[0];
    },
    beta() {
      return (
        authorizationStore.getters.isBeta || this.$store.getters.workspaceBeta
      );
    },
    isSaving() {
      return this.$store.getters.isSaving;
    },
    workspaceModules() {
      return this.$store.getters.workspaceModules;
    },
    vectorId() {
      return this.$store.state.vectorId;
    },
    moduleId() {
      return this.$store.state.vectorId.toString();
    },
    information() {
      return this.$store.state.canvas.information;
    },
    informationWithSystem() {
      const system = [
        {
          id: "#createdBy",
          type: 20,
          displayName: "Created By",
          system: true
        },
        {
          id: "#updatedBy",
          type: 20,
          displayName: "Last Change By",
          system: true
        },
        {
          id: "#assignees",
          type: 6,
          displayName: "Assignees",
          system: true
        }
      ];
      return Array.isArray(this.$store.state.canvas.information)
        ? [...this.$store.state.canvas.information, ...system]
        : system;
    },
    standardActivities() {
      return this.$store.state.canvas.standardActivities;
    },
    activities() {
      return this.$store.state.canvas.activities;
    },
    shareActivities() {
      return [
        { id: "view", name: "View" },
        ...this.$store.state.canvas.activities
      ];
    },
    states() {
      return this.$store.state.canvas.states;
    },
    entryTemplates() {
      if (
        this.$store.state.canvas.information &&
        this.$store.state.canvas.documents
      ) {
        return [
          ...this.$store.state.canvas.information
            .filter(x => x.type >= 10 && x.type <= 13)
            .map(function(x) {
              return { id: x.id, name: x.displayName };
            }),
          ...this.$store.state.canvas.documents
        ];
      }
      return [];
    },
    menus() {
      return this.$store.getters.canvasMenus;
    },
    informationSource() {
      return queryLogic.querySelectionPrefix
        .concat(this.information)
        .concat(queryLogic.querySelectionSuffix);
    },
    pageAvailability() {
      return (
        this.formData &&
        ((this.formData.actionType === "advanced" && this.beta) ||
          this.formData.actionType !== "advanced")
      );
    },
    getInformation() {
      return this.information.find(x => x.id === this.formData.item);
    },
    getState() {
      return this.states.find(x => x.id === this.formData.item);
    },
    getStandardActivity() {
      return this.standardActivities.find(x => x.id === this.formData.item);
    },
    getActivity() {
      return this.activities.find(x => x.id === this.formData.item);
    },
    title() {
      if (this.isSchedule) return this.formData.name || "Schedule";
      if (!this.formData.item) return this.formData.type;

      try {
        switch (this.formData.type) {
          case "state":
            return this.getState && this.getState.name;
          case "activity":
            return this.getActivity && this.getActivity.name;
          case "standardActivity":
            return this.getStandardActivity && this.getStandardActivity.name;
          case "information":
            return this.getInformation && this.getInformation.displayName;
        }
      } catch (err) {
        console.error("AutomationDesign:Title");
      }
    },
    channels() {
      let channels = [Channel.AppNotification, Channel.Email];

      if (this.beta) channels = channels.concat([Channel.SMS]);

      if (this.isInternal)
        channels = channels.concat([
          Channel.Slack,
          Channel.WhatsApp,
          Channel.CloudMessage
        ]);

      return channels;
    },
    eventType() {
      try {
        switch (this.formData.type) {
          case "vector":
            return "vector";
          case "activities":
            return "activityConsole";
          case "standardActivities":
            return "standardConsole";
          case "states":
            return "stateConsole";
          case "state":
            return "state";
          case "activity":
            return "activity";
          case "standardActivity":
            return "standardActivity";
          case "information":
            if (!this.formData.item) return "informationConsole";
            const informationType =
              this.getInformation && this.getInformation.type;
            return informationType &&
              (informationType == 5 || informationType == 6)
              ? "due"
              : "information";
        }
      } catch (err) {
        console.error("AutomationDesign:EventType", err);
      }
    },
    eventBadges() {
      let badges = [];
      if (this.isSchedule) {
        badges.push(this.scheduleDigest
          ? { name: "Digest", description: "one notification listing the matching entries, to fixed people" }
          : { name: "For each entry", description: "the action runs once per matching entry" });
        if (this.formData.convertedFrom)
          badges.push({ name: "Converted", description: "converted from a timed automation by an administrator" });
        return badges;
      }
      if (this.formData.general)
        badges.push({
          name: "Execute Once",
          description:
            "execute exactly one time for each trigger without iterate through entries"
        });
      if (this.formData.condition)
        badges.push({
          name: "Condition",
          description: "only trigger when condition is met"
        });
      if (this.formData.timeModifier)
        badges.push({
          name: "Timing",
          description: "only trigger when the timing is met"
        });
      if (this.formData.repeat)
        badges.push({
          name: "Recurring",
          description: "trigger repeatedly when the recurring condition is met"
        });
      if (badges.length) return badges;
    },
    getAutomation() {
      /* vector */
      if (this.formData.type === "vector")
        return this.$store.getters.vectorTriggers.filter(
          x => x.type === "vector"
        );
      /* console element */
      if (this.formData.type && !this.formData.item)
        return this.$store.getters.vectorTriggers.filter(
          x => x.type === this.formData.type && !x.item
        );
      /* default */
      return this.$store.getters.vectorTriggers.filter(
        x => x.item === this.formData.item
      );
    },
    recurringMonthDay: {
      get() {
        return Boolean(this.formData.recurringMonthDay);
      },
      set(val) {
        this.$set(this.formData, "recurringMonthDay", val);
        if (val) {
          if (!this.recurringMonthDayEvery) {
            this.$set(this.formData, "recurringMonthDayEvery", 1);
          }
          this.$refs.eventChecklistInteger.$el
            .getElementsByTagName("input")[0]
            .focus();
        } else if (!val && this.formData.recurringMonthDayEvery) {
          this.$delete(this.formData, "recurringMonthDayEvery");
        }
      }
    },
    recurringMonthDayEvery: {
      get() {
        return this.formData.recurringMonthDayEvery;
      },
      set(val) {
        this.$set(this.formData, "recurringMonthDayEvery", val);
        if (val) {
          this.$set(this.formData, "recurringMonthDay", true);
        } else {
          this.$delete(this.formData, "recurringMonthDay");
        }
      }
    },
    actionTypeAdvanced: {
      get() {
        return this.formData.actionType === "advanced";
      },
      set(val) {
        if (val) {
          this.$set(this.formData, "actionType", "advanced");
        } else {
          this.$set(this.formData, "actionType", "automation");
          this.selectActionType("automation");
        }
      }
    }
  },
  methods: {
    moment,
    onAdvancedFullViewChange(key, value) {
      this.$set(this.advancedFullViewMap, key, value);
    },
    /**
     * @param {Object} automation
     * @param {?Object} param
     * @param {string} param.step
     */
    async show(automation, param = {}) {
      /* get */
      if (automation.id) {
        const findAutomation = this.$store.getters.vectorTriggers.find(
          x => x.id === automation.id
        );
        if (findAutomation) {
          this.formData = _.cloneDeep(findAutomation);
        }
      } else {
        this.formData = automation;
      }
      /* schedule: fill what an older or converted template lacks (never overwrites) */
      if (isSchedule(this.formData)) {
        normalizeTemplate(this.formData, this.$set);
      }
      /* initial:enabled */
      if (this.formData.enabled !== false) {
        this.$set(this.formData, "enabled", true);
      }
      /* initial:action type */
      if (!this.formData.actionType && (!this.formData.id || this.formData.notification)) {
        this.$set(this.formData, "actionType", Action_Type.Notification);
      }
      this.selectActionType(this.formData.actionType);
      /* initial:channel */
      if (!this.formData.channel && this.formData.actionType == Action_Type.Notification) {
        this.$set(this.formData, "channel", Channel.AppNotification.value);
      }
      /* initial:recurring */
      if (this.formData.repeat) {
        const startOfDay = moment().startOf("day");
        if (!this.formData.startsOn) {
          this.$set(this.formData, "startsOn", startOfDay);
        } else if (moment(this.formData.startsOn).isBefore(startOfDay)) {
          let startsOn = moment(this.formData.startsOn);
          startsOn.date(startOfDay.date());
          startsOn.month(startOfDay.month());
          startsOn.year(startOfDay.year());
          this.$set(this.formData, "startsOn", startsOn.format());
        }
      }
      /* initial:automation block */
      if (this.formData.actionType == Action_Type.Automation) {
        /* a schedule's blocks always run per entry, after nothing: open them directly */
        if (isSchedule(this.formData) && !this.automationType) {
          this.automationType = "after";
        }
        this.setupBlockInitial(
          this.automationType == "before" ? "automationBefore" : "automation"
        );
      }
      
      if (this.formData.id) {
        const { t, tid } = this.$route.query;
        if (!t && !tid) {
          let queryType = "notification";

          if (this.formData.actionType === Action_Type.Automation) {
            queryType = "automation";
          } else if (this.formData.actionType === Action_Type.Advanced) {
            queryType = "script";
          }

          commons.addDesignQueryParams(this.$router, this.$route, [
            { name: "t", value: queryType },
            { name: "tid", value: this.formData.id }
          ])
        }
      }

      return new Promise(resolve => {
        this.resolve = resolve;
        this.$refs.ftsDesignModal.show({
          beforeCloseHandler: () => {
            const diff = changesets.diff(this.formData, this.originalData);
            if (diff && diff.length) {
              this.$modal.show("dialog", {
                title: "Confirm discard changes",
                text: "Discard all changes?",
                buttons: [
                  { title: "Cancel" },
                  {
                    title: "Discard",
                    default: true,
                    handler: () => {
                      this.$modal.hide("dialog");
                      this.reset();
                    }
                  }
                ]
              });
            } else {
              this.reset();
            }
          }
        });
        /* params.step (not for a schedule: it has no condition scripts, and the steps would switch
           per-trigger settings on) */
        if (this.beta && param && param.step && !this.isSchedule) {
          if (param.step === "conditionScript") {
            this.advanced = true;
            this.$set(this.formData, "condition", true);
            this.$nextTick(() => {
              this.$refs.conditionScript && this.$refs.conditionScript.$el.scrollIntoView();
            });
          } else if (param.step === "queryScript") {
            this.advanced = true;
            this.$set(this.formData, "condition", true);
            this.$nextTick(() => {
              this.$refs.queryScript && this.$refs.queryScript.$el.scrollIntoView();
            });
          } else if (param.step === "recipientScript") {
            this.advanced = true;
            this.$set(this.formData, "enableRecipientScript", true);
            this.$nextTick(() => {
              this.$refs.recipientScript && this.$refs.recipientScript.$el.scrollIntoView();
            });
          } else if (param.step === "subjectTemplate" && this.formData.actionType === "notification") {
            this.advanced = true;
            this.$set(this.formData, "customizeMessage", true);
            this.$nextTick(() => {
              this.$refs.customizeMessage && this.$refs.customizeMessage.scrollIntoView();
            });
          } else if (param.step === "template" && this.formData.actionType === "notification") {
            this.advanced = true;
            this.$set(this.formData, "customizeMessage", true);
            this.$nextTick(() => {
              this.$refs.template && this.$refs.template.$el.scrollIntoView();
            });
          }
        }

        this.$nextTick(() => {
          this.originalData = _.cloneDeep(this.formData);
        });
      });
    },
    // Master plan (2026-08-01, refined same day): the save gate keys on the DESIGN's
    // serverNative flag, not the user — a server-native graph must stay native-servable, so
    // normal users cannot save one that would fall back to Python. A graph an internal /
    // consultant user relaxed to the Python lane (flag removed) is freely configurable by
    // everyone. Internal identities / Consultant role are always exempt (they get the
    // designer's toggle warning instead). Classify failures fail-open: a network error
    // must never block design work — the runtime lane fallback still protects execution.
    canRelaxServerNative() {
      const roles = authorizationStore.getters.userRoles || [];
      return authorizationStore.getters.isInternal || roles.indexOf("Consultant") > -1;
    },
    // Normal users are blocked outright; internal/consultant users are prompted and may
    // choose to proceed (master plan confirmation 2026-08-01 #2).
    async confirmPythonLaneViolation(reason) {
      if (!this.canRelaxServerNative()) {
        this.$toasted.error(
          `Can't save: ${reason} Ask a consultant to switch this automation off server-native if the Python engine is truly needed.`);
        return false;
      }
      const res = await this.$confirmation.show({
        title: "Needs the Python engine",
        text: `${reason} The automation stays server-native and will run on the Python engine. Save anyway?`,
      });
      return res === true;
    },
    async assertServerNativeCompatible() {
      for (const automationType of ["automation", "automationBefore"]) {
        const container = this.formData[automationType];
        if (!container || container.serverNative !== true) continue;
        if (!container.blocks || !Object.keys(container.blocks).length) continue;

        // same variables/priorVariables strip as the designer's classify payload
        const snapshot = _.cloneDeep(container.blocks);
        Object.values(snapshot).forEach((node) => {
          if (node.name != "start" && node.data) {
            delete node.data.variables;
            delete node.data.priorVariables;
            if (node.data.actions) {
              node.data.actions.forEach((action) => {
                delete action.variables;
                delete action.priorVariables;
              });
            }
          }
        });

        try {
          const res = await this.$store.dispatch("classifyAutomationNative", {
            vectorId: this.$store.state.vectorId,
            activityId: this.formData.item || null,
            isForm: false,
            automationBlocks: { blocks: snapshot },
          });
          const verdict = res && res.data;
          if (verdict && verdict.compatible === false) {
            // the standard spellings that take the graph native, offered before the refusal
            if (await offerBlockRewrites(this, verdict, container.blocks)) continue;
            const blockers = (verdict.findings || [])
              .map((f) => f.reason)
              .filter(Boolean)
              .join(" • ") || verdict.reason || "unsupported construct";
            if (!(await this.confirmPythonLaneViolation(
              `this automation would need the Python engine (${blockers}).`)))
              return false;
          }
        } catch (err) {
          /* advisory endpoint unavailable — fail-open */
        }
      }

      return true;
    },
    async done(save) {
      if (!this.recurringIsComplete()) return;
      if (this.isSchedule && !(await this.scheduleIsComplete())) return;
      if (!(await this.assertServerNativeCompatible())) return;

      if (!this.formData.id) {
        this.$set(this.formData, "id", commons.newUUID());
      }
      if (this.isSchedule) {
        prepareForSave(this.formData);
      }

      const formData = _.cloneDeep(this.formData);
      this.$store.dispatch("setVectorTrigger", formData);

      if (save) {
        this.originalData = _.cloneDeep(formData);
        window.eventBus.$emit("studio.save");
        /* the module save re-syncs the schedule's routine server-side; refresh its status after */
        const designer = this.$refs.scheduleDesigner;
        if (this.isSchedule && designer && typeof designer.afterSave === "function") {
          designer.afterSave();
        }
      } else {
        this.resolve && this.resolve(formData);
        this.reset();
      }
    },
    /* SS05969: a monthly recurrence with none of the three day options ticked — or a weekly
       one with no weekday ticked — carries no day to recur on. The scheduler cannot infer
       one, so it falls back to the anchor date the automation happened to be saved on, which
       is never what the author meant. Ask for the day here rather than let it through. */
    recurringIsComplete() {
      if (!this.formData.repeat) return true;

      if (this.formData.recurringType === "month"
        && !this.formData.recurringMonthStart
        && !this.formData.recurringMonthEnd
        && !(this.formData.recurringMonthDay && this.formData.recurringMonthDayEvery)) {
        this.$toasted.error("Pick which day of the month the automation should recur on.");
        return false;
      }

      if (this.formData.recurringType === "week"
        && !(this.formData.recurringWeeks && this.formData.recurringWeeks.length)) {
        this.$toasted.error("Pick at least one day of the week for the automation to recur on.");
        return false;
      }

      return true;
    },
    /* A schedule is saved only when it can run: the designer checks it (and asks the server) first.
       Without the designer on screen (an advanced action a non-beta user cannot open), the local
       checks alone decide. */
    async scheduleIsComplete() {
      const designer = this.$refs.scheduleDesigner;
      if (designer && typeof designer.beforeSave === "function") return designer.beforeSave();

      const problems = validateTemplate(this.formData);
      if (problems.length) {
        this.$toasted.error(`Can't save the schedule: ${problems[0]}`);
        return false;
      }
      return true;
    },
    /* the schedule designer's "Then" picks the per-entry action */
    setScheduleActionType(actionType) {
      this.$set(this.formData, "actionType", actionType);
      this.selectActionType(actionType);
      if (actionType === Action_Type.Notification && !this.formData.channel) {
        this.$set(this.formData, "channel", Channel.AppNotification.value);
      }
      if (actionType === Action_Type.Automation && !this.automationType) {
        this.automationType = "after";
      }
    },
    beforeClose() {
      this.$refs.ftsDesignModal.beforeClose();
    },
    remove() {
      this.$modal.show("dialog", {
        title: "Confirmation",
        text: this.isSchedule ? "Delete schedule? It stops running once the module is saved." : "Delete automation?",
        buttons: [
          { title: "Cancel" },
          {
            title: "Delete",
            default: true,
            handler: () => {
              this.$modal.hide("dialog");
              this.$store.dispatch("removeVectorTrigger", this.formData.id);
              this.resolve && this.resolve(true);
              this.reset();
            }
          }
        ]
      });
    },
    reset() {
      const { t, tid } = this.$route.query;
      if (["automation", "notification", "script"].includes(t) && tid === this.formData.id) {
        commons.removeDesignQueryParams(this.$router, this.$route, ["t", "tid"]);
      }

      this.$refs.ftsDesignModal.close();
      Object.assign(this.$data, this.$options.data.apply(this));
    },
    afterShow() {
      /* a schedule has no trigger event to pick (and none may be written onto it) */
      if (this.isSchedule) return;

      switch (this.formData.type) {
        case "state":
          this.triggers = [
            { id: "changeTo", name: "changed to" },
            { id: "changeFrom", name: "changed from" }
          ];

          if (this.formData.trigger) break;

        case "activity":
          this.triggers = [{ id: "execute", name: "performing" }];
          break;

        case "standardActivity":
          this.triggers = [{ id: "execute", name: "performing" }];
          break;

        case "information":
          this.triggers = [{ id: "change", name: "changed" }];

          if (this.formData.item) {
            let informationItem = this.information.find(
              x => x.id == this.formData.item
            );
            if (
              informationItem != null &&
              (informationItem.type == 5 || informationItem.type == 6)
            ) {
              this.triggers = [
                { id: "target", name: "due" },
                { id: "change", name: "changed" }
              ];
            }
          }
          break;
      }
      if (!this.triggers.some(x => x.id == this.formData.trigger)) {
        this.formData.trigger = this.triggers[0].id;
      }
    },
    duplicate() {
      let cloned = _.cloneDeep(this.formData);
      delete cloned.id;
      /* a copied schedule starts switched off: two live copies would each act on every match */
      if (isSchedule(cloned)) {
        cloned.enabled = false;
        cloned.name = `${cloned.name || "Schedule"} (Copy)`;
        delete cloned.convertedFrom;
        delete cloned.convertedOn;
        this.$store.dispatch("setVectorTrigger", cloned);
        this.$toasted.success("Copied. The copy is switched off until you turn it on.");
        this.resolve && this.resolve(cloned);
        this.reset();
        return;
      }
      if (cloned.description) {
        cloned.description = `${cloned.description} (Copy)`;
      } else {
        const actionType = this.actionTypes.find(
          x => x.id === cloned.actionType
        );
        if (actionType && actionType.name)
          cloned.description = `${actionType.name} (Copy)`;
        else cloned.description = `Automation (Copy)`;
      }

      function setDescription(automation, i) {
        const description = `${cloned.description} (${i})`;
        if (automation.some(x => x.description === description))
          return setDescription(automation, i + 1);
        else return description;
      }

      try {
        const automation = this.getAutomation.filter(
          x => x.description === cloned.description
        );
        if (automation.length)
          cloned.description = setDescription(
            this.getAutomation,
            automation.length
          );
      } catch (er) {
        console.error("AutomationDesign:Duplicate", err);
      }

      this.$store.dispatch("setVectorTrigger", cloned);
      this.resolve && this.resolve(cloned);
      this.reset();
    },
    informationIcon(id) {
      return commons.informationIcon(id) || "fts-icon-text";
    },
    /* recipients */
    async loadRecipients(search) {
      search = search || "";
      let vm = this;
      let result = await vm.$store.dispatch("searchRecipient", {
        search: search,
        workspaceId: vm.$store.state.vector.ownerWorkspaceId
      });

      var types = [20, 21, 0, 16, 25, 26];

      let information = vm.$store.state.canvas.information
        .filter(x => {
          return (
            types.some(y => y == x.type) &&
            x.displayName.toLowerCase().indexOf(search.toLowerCase()) > -1
          );
        })
        .map(x => {
          return {
            id: x.id,
            recipientType: 5,
            informationType: x.type,
            name: x.displayName
          };
        });

      information = information.concat(
        [
          {
            id: "#createdBy",
            type: 20,
            name: "Created By"
          },
          {
            id: "#updatedBy",
            type: 20,
            name: "Last Change By"
          },
          {
            id: "#assignees",
            type: 6,
            name: "Assignees"
          }
        ].filter(x => x.name.toLowerCase().indexOf(search.toLowerCase()) > -1)
      );

      vm.recipientOptions = _.orderBy(
        result.data.concat(information),
        ["type", x => x.name && x.name.toLowerCase()],
        ["desc", "asc"]
      );
    },
    /* action type */
    selectActionType(item) {
      if (item == Action_Type.Notification) {
        if (!this.formData.notification) {
          this.$set(this.formData, "notification", true);
        }
      } else if (this.formData.hasOwnProperty("notification")) {
        this.$delete(this.formData, "notification");
      }
      /* initial:automation block type */
      if (item == Action_Type.Automation && !this.automationType) {
        if (this.formData.automationBefore) {
          this.automationType = "before";
        } else if (this.formData.automation) {
          this.automationType = "after";
        }
      }
    },
    /* attachment */
    addAttachment() {
      let obj = {
        id: commons.nanoID()
      };
      if (Array.isArray(this.formData.attachments))
        this.formData.attachments.push(obj);
      else this.$set(this.formData, "attachments", [obj]);
    },
    removeAttachment(data) {
      let tableData = this.formData.attachments;
      tableData.splice(tableData.indexOf(data), 1);
    },
    openAttachmentModal(data) {
      if (data.configuration == null) {
        data.configuration = {
          filter: { items: [] }
        };
      }

      this.attachmentFormData = _.cloneDeep(data);
      this.$refs.attachmentModal.show();
    },
    updateAttachmentFormData(data) {
      let attachment = this.formData.attachments.find(
        x => x.id === this.attachmentFormData.id
      );
      Object.assign(attachment, data);
      this.$refs.attachmentModal.close();
    },
    closeAttachmentModal() {
      this.attachmentFormData = {};
    },
    /* test automation */
    async deleteAutomation(automationType) {
      const res = await this.$confirmation.show({
        title: "Automation",
        text: automationType === "automationBefore" ? "Do before" : "Do after",
        type: "delete"
      });

      if (res === true) {
        this.formData[automationType] = null;
        this.automationType = null;
      }
    },
    async testAutomation() {
      this.previewSending = true;
      if (this.$refs.advancedDebugPanel) {
        this.$refs.advancedDebugPanel.clear();
      }
      try {
        await this.$store.dispatch("testAutomation", this.formData);
        this.formData.actionType === "notification"
          ? this.$toasted.success("Notification has been sent")
          : this.$toasted.success("Script has been executed");
      } catch (err) {
        if (this.$refs.advancedDebugPanel) {
          this.$refs.advancedDebugPanel.logError(err);
        }
        this.$toasted.global.error({
          response: err.response
        });
        console.error("AutomationDesign:TestAutomation", err);
      } finally {
        this.previewSending = false;
      }
    },
    testLocusDetailChanged() {
      this.$delete(this.formData, "testLocusDetail");
      this.$delete(this.formData, "testLocusDetailId");
    },
    testActivityMatchValidation(sourceActivityId, targetActivityId) {
      if (
        (this.formData.type === "information" && this.formData.trigger === "change") || 
        (this.formData.type === "state" && ["changeTo", "changeFrom"].includes(this.formData.trigger))
      ) {
        return true;
      }

      if (sourceActivityId === "createEditView") {
        return ["create", "edit"].includes(targetActivityId);
      }

      return sourceActivityId === targetActivityId;
    },
    /* preview */
    async preview() {
      this.previewLoading = true;
      try {
        let res = await this.$store.dispatch(
          "previewAutomation",
          this.formData
        );
        this.notificationPreview = res.data;
        /* recipients */
        if (this.notificationPreview && this.notificationPreview.recipients) {
          this.notificationPreview.recipients = [
            ...new Set(
              this.notificationPreview.recipients.map(x => {
                const res = this.$store.state.users.find(y => y.profileId == x);
                return res && res.username ? res.username : x;
              })
            )
          ];
        }
        /* content */
        this.$nextTick(() => {
          try {
            let iframe = this.$refs.notificationContentIframe;
            iframe.height = "auto";
            iframe.contentWindow.document.body.style.margin = 0;
            iframe.contentWindow.document.body.style.fontFamily =
              "var(--fts-font-family)";
            iframe.contentWindow.document.body.innerHTML = this.notificationPreview.content;
            iframe.height = iframe.contentWindow.document.body.offsetHeight;
          } catch (err) {
            console.warn("[DOMException]", err);
          }
        });
      } catch (err) {
        this.$toasted.global.error({
          response: err.response
        });
        console.error("AutomationDesign:Preview", err);
      } finally {
        this.previewLoading = false;
      }
    },
    setupBlockInitial(automationType) {
      if (
        this.formData[automationType] &&
        this.formData[automationType].blocks
      ) {
        this.formData[automationType].blocks[1].data.variables = [
          {
            id: "form",
            fieldName: "form",
            name: "Form",
            activityId: null,
            nodeId: 1
          },
          {
            id: "entry",
            fieldName: "entry",
            name: "Entry",
            nodeId: 1
          }
        ];

        /* no form: nothing is being submitted (a schedule acts on the matched entry alone) */
        if (
          this.formData.type == "information" ||
          this.formData.type == "state" ||
          isSchedule(this.formData)
        ) {
          this.formData[automationType].blocks[1].data.variables.splice(0, 1);
        } else {
        }

        if (
          automationType == "automationBefore" &&
          (this.formData.item == "create" ||
            this.formData.item == "createEditView")
        ) {
          console.log("remove entry");
          this.formData[automationType].blocks[1].data.variables.splice(1, 1);
        }
      }
    },
    /* setup automation */
    setupAutomation(automationType) {
      if (!this.formData[automationType]) {
        Vue.set(this.formData, automationType, {
          // master plan (2026-08-01): new automation starts on the server-native engine
          serverNative: true,
          testLocusId: null,
          testLocus: null,
          testLocusDetailId: null,
          testLocusDetail: null
        });

        this.formData[automationType].blocks = {
          1: {
            id: 1,
            name: "start",
            data: {
              id: "start",
              variables: [
                {
                  id: "form",
                  fieldName: "form",
                  name: "Form",
                  activityId: null,
                  nodeId: 1
                },
                {
                  id: "entry",
                  fieldName: "entry",
                  name: "Entry",
                  nodeId: 1
                }
              ]
            },
            class: "start",
            html: "start",
            typenode: "vue",
            outputs: {
              output_1: {
                connections: []
              }
            },
            inputs: {},
            pos_x: 200,
            pos_y: 50
          }
        };

        /* no form: nothing is being submitted (a schedule acts on the matched entry alone) */
        if (
          this.formData.type == "information" ||
          this.formData.type == "state" ||
          isSchedule(this.formData)
        ) {
          this.formData[automationType].blocks[1].data.variables.splice(0, 1);
        } else {
        }

        if (
          automationType == "automationBefore" &&
          (this.formData.item == "create" ||
            this.formData.item == "createEditView")
        ) {
          console.log("remove entry");
          this.formData[automationType].blocks[1].data.variables.splice(1, 1);
        }
      }

      if (
        this.formData[automationType].blocks[1].data.variables &&
        this.formData[automationType].blocks[1].data.variables.length
      ) {
        let form = this.formData[automationType].blocks[1].data.variables.find(
          x => x.id == "form"
        );
        if (form) {
          form.type = 35;
          form.moduleId = this.vectorId.toString();
          form.activityId = this.formData.item;
        }

        let entry = this.formData[automationType].blocks[1].data.variables.find(
          x => x.id == "entry"
        );
        if (entry) {
          entry.type = 7;
          entry.moduleId = this.vectorId.toString();
        }
      }
    },
    customRecipient(x) {
      return {
        identity: x,
        name: x,
        recipientType: 8
      };
    },
    customRecipientSelectable(option) {
      if (option.recipientType === 8) {
        return !this.customRecipientError(option);
      }
      return true;
    },
    customRecipientError(option) {
      return !emailValidator(option.identity);
    },
    onTimingToggleChange() {
      if (!this.formData.timeModifier) {
        this.formData.targetTime = null;
        this.formData.multipler = null;
        this.formData.durationType = null;
      }
    }
  },
  watch: {
    "formData.actionType": function(val) {
      if (val == "automation") {
        if (this.automationType == "before") {
          this.setupAutomation("automationBefore");
        } else if (this.automationType == "after") {
          this.setupAutomation("automation");
        }
      }
    },
    automationType: function(val) {
      if (this.automationType == "before") {
        this.setupBlockInitial("automationBefore");
        this.setupAutomation("automationBefore");
      } else if (this.automationType == "after") {
        this.setupBlockInitial("automation");
        this.setupAutomation("automation");
      }
    }
  }
};
</script>

<style lang="scss">
@use "./src/assets/scss/vars" as *;
@use "./src/assets/scss/colors" as *;

.fts-event-config-modal {
  /* legacy timed automation: points at Schedule (docs/scheduling-routine-automation-proposal.md §2) */
  .event-schedule-hint {
    display: flex;
    gap: 0.5rem;
    align-items: baseline;
    margin-bottom: 1rem;
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--fts-gray-200);
    border-radius: 0.375rem;
    background: var(--fts-gray-50);
    color: var(--fts-default);
    font-size: 0.8125rem;
  }
  /* event toggle label */
  .event-toggle-label {
    margin-right: 0.375rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--fts-form-label);
  }
  /* event type */
  .event-type {
    min-width: 0;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    font-weight: 500;
    font-size: 1.125rem;
    line-height: 1.5;
    .event-type-activity {
      min-width: 0;
      display: flex;
      align-items: center;
    }
    .event-type-information {
      min-width: 0;
      display: flex;
      align-items: center;
    }
    .event-type-state {
      > div {
        padding: 0.094rem 0.75rem;
        font-size: 1rem;
      }
    }
    .event-type-emoji {
      margin-right: 0.375rem;
    }
    .event-type-icon {
      margin-right: 0.375rem;
      line-height: inherit;
      color: var(--fts-secondary);
    }
    .event-type-label-ellipsis {
      @include text-overflow-ellipsis;
    }
    .event-type-select {
      display: inline-flex;
      &.event-type-select--bordered {
        .event-type-select__toggle {
          margin-top: 0.25rem;
          margin-bottom: 0.25rem;
          padding: 0.125rem 0.5rem;
          border: 1px solid var(--fts-gray-200);
          border-radius: 0.25rem;
        }
      }
      &:not(.event-type-select--bordered) {
        position: relative;
        &::before {
          content: '';
            position: absolute;
            left: 0;
            bottom: 1.5px;
            width: 100%;
            height: 1px;
            background-color: var(--fts-border);
        }
      }
      .event-type-select__toggle {
        .event-type-toggle-icon {
          margin-left: 0.125rem;
          font-size: 0.875rem;
        }
      }
    }
  }
  /* event badges */
  .event-badges {
    @include flex-gap($display: flex, $gap: 0.375rem);
    padding-top: 0.375rem;
  }
  /* event channel */
  .event-channel-container {
    margin-top: 0.325rem;
    margin-bottom: 0.325rem;
    .event-channel-icon {
      margin-right: 0.25rem;
    }
  }
  /* event test action */
  .event-test {
    .event-test-legend {
      margin-bottom: 1.5rem;
      font-size: 0.875rem;
      color: var(--fts-form-label);
    }
    padding: 1rem;
    border: 1px solid var(--fts-gray-200);
    border-radius: 0.25rem;
    .event-test-selector {
      .fts-form-group {
        margin-bottom: 1rem;
      }
    }
    .event-test-action {
      display: flex;
      align-items: center;
      > *:not(:first-child) {
        margin-left: 0.5rem;
      }
    }
    .event-test-action-helper {
      margin-top: 0.5rem;
      font-size: 0.75rem;
      color: var(--fts-form-helper);
      line-height: 1rem;
    }
  }
  /* event attachment */
  .event-attachment {
    display: flex;
    align-items: center;
    padding-bottom: 0.75rem;
    .event-attachment-selection {
      flex: 1;
      margin-right: 0.75rem;
    }
  }
  /* event preview */
  .event-preview {
    margin-top: 2rem;
    padding-bottom: 1rem;
    .event-preview-wrapper {
      &:not(:last-child) {
        margin-bottom: 1.5rem;
      }
    }
    .event-preview-iframe {
      width: 100%;
      border: 0;
    }
    .event-preview-subject-wrapper {
      .event-preview-subject-badge {
        vertical-align: bottom;
      }
      .event-preview-subject {
        font-weight: 500;
        font-size: 1rem;
        line-height: 1.5rem;
      }
    }
    .event-preview-recipient-container {
      @include flex-gap($display: flex, $gap: 0.5rem);
      align-items: center;
      margin-top: 0.025rem;
      font-size: 0.875rem;
      line-height: 1.25rem;
      .event-preview-recipient {
        @include text-overflow-ellipsis;
        padding: 0.25rem 0.375rem;
        border-radius: 10em;
        background-color: var(--fts-gray-200);
      }
      .event-preview-recipient-tippy {
        min-width: 0;
      }
    }
    .event-preview-content {
      padding-top: 1.5rem;
      border-top: 1px solid var(--fts-gray-200);
    }
  }
  /* event automation selection */
  .event-automation-selection {
    margin-bottom: 1rem;
    .automation-design-toggle {
      @include user-select-none;
      display: inline-flex;
      align-items: center;
      padding: 0.135rem 0.75rem;
      border-bottom: 2px solid transparent;
      background-color: var(--fts-gray-100);
      font-size: 0.875rem;
      font-weight: 500;
      line-height: 2.5;
      cursor: pointer;
      &:not(.automation-design-toggle--active) {
        &:hover {
          background-color: var(--fts-gray-200);
        }
      }
      &.automation-design-toggle--active {
        @include theme() {
          background: rgba($color: theme-get("fts-primary"), $alpha: 0.095);
        }
        border-bottom-color: var(--fts-primary);
        color: var(--fts-primary);
      }
      .automation-design-toggle__action {
        margin-left: 0.375rem;
      }
    }
  }
  /* event checkbox */
  .event-checkbox {
    width: auto;
    margin: 0 1.175rem 0 0.575rem;
    .m-chckbox--label {
      padding: 0;
    }
  }
  /* event checklist */
  .design-modal-form-label + .event-checklist {
    margin-top: 0.75rem;
  }
  .event-checklist {
    .event-checklist-item {
      display: flex;
      align-items: center;
      margin-bottom: 1rem;
      .event-checklist-item-label {
        @include user-select-none;
        cursor: pointer;
      }
    }
    .event-checklist-integer {
      > .fts-form-control {
        width: 60px;
      }
    }
  }

  .event-unavailable-message {
    max-width: 768px;
    margin: 2rem auto;
    padding: 1rem;
    border-radius: 0.25rem;
    background-color: var(--fts-gray-100);
    font-size: 0.875rem;
    line-height: 1.5;
  }
  .event-unavailable-message-title {
    margin-bottom: 0.375rem;
    font-weight: 500;
  }
}
// event type content
.event-type-content.event-type-dropdown-menu {
  font-size: 1rem;
}
.recipient-option-error {
  color: var(--fts-danger);
  font-size: 0.75rem;
  line-height: 1rem;
}
</style>
